import React, { useState, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Upload, Image as ImageIcon, Search, Pencil } from 'lucide-react';
import { PromoImage } from '../../types';
import { promoImagesService } from '../../services/promoImagesService';
import { PROMO_FILTRY, PromoFiltr, sedneNaFiltr } from '../../utils/promoSkupiny';
import { brandsService } from '../../services/brandsService';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { formatDate } from '../../utils/helpers';

// Doporučené rozměry/poměr obrázku pro každý typ slotu (z geometrie PDF, ~300 dpi).
// Viz docs/navrh-konzistence-slotu-navrh-vs-pdf.md.
const SIZE_SPECS: Record<string, { ratio: number; w: number; h: number; label: string }> = {
  single:     { ratio: 1184 / 833,  w: 1184, h: 833,  label: '1×1' },
  horizontal: { ratio: 2380 / 833,  w: 2380, h: 833,  label: '2×1' },
  square:     { ratio: 2380 / 1678, w: 2380, h: 1678, label: '2×2' },
  full_page:  { ratio: 2380 / 3364, w: 2380, h: 3364, label: 'celá stránka' },
  footer:     { ratio: 2380 / 228,  w: 2380, h: 228,  label: 'patička' },
  header_2x1: { ratio: 2380 / 605,  w: 2380, h: 605,  label: 'hlavička 2×1' },
  header_2x2: { ratio: 2380 / 1450, w: 2380, h: 1450, label: 'hlavička 2×2' },
};

// Tolerance shody poměru stran (±5 %) - v rámci ní se obrázek považuje za sedící.
const RATIO_TOLERANCE = 0.05;

const getImageDimensions = (src: string): Promise<{ w: number; h: number }> =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = reject;
    img.src = src;
  });

// Měkká kontrola poměru stran vůči doporučení pro daný typ. Vrací true = lze pokračovat
// (poměr v toleranci, nebo uživatel v dialogu potvrdil), false = uživatel zrušil.
// Symetrická relativní odchylka (vůči průměru obou poměrů). imgSource = data URL (nový soubor)
// nebo URL existujícího obrázku (při editaci typu).
const confirmRatioOk = async (imgSource: string, size: string): Promise<boolean> => {
  const spec = SIZE_SPECS[size];
  if (!spec || !imgSource) return true;
  try {
    const { w, h } = await getImageDimensions(imgSource);
    const imgRatio = w / h;
    const off = Math.abs(imgRatio - spec.ratio) / ((imgRatio + spec.ratio) / 2);
    if (off > RATIO_TOLERANCE) {
      return window.confirm(
        `Poměr stran obrázku (${w}×${h} px = ${imgRatio.toFixed(2)} : 1) neodpovídá ` +
        `doporučenému pro typ „${spec.label}" (${spec.ratio.toFixed(2)} : 1, např. ${spec.w}×${spec.h} px).\n\n` +
        `V letáku se obrázek zmenší tak, aby byl celý vidět → vzniknou bílé okraje. ` +
        `Chcete přesto pokračovat?`
      );
    }
  } catch {
    // Rozměry se nepodařilo načíst - kontrolu přeskočíme, akci nezablokujeme.
  }
  return true;
};

export const PromoImagesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuthStore();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // Po výměně souboru zůstává URL obrázku stejná, takže by prohlížeč chvíli
  // ukazoval starou verzi z cache. Razítko v URL ho donutí načíst novou.
  const [imageStamps, setImageStamps] = useState<Record<string, number>>({});
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<'single' | 'horizontal' | 'square' | 'full_page' | 'footer' | 'header_2x1' | 'header_2x2'>('single');
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const [isForEndUsers, setIsForEndUsers] = useState(false);
  const [fillDate, setFillDate] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Stejný filtr jako v paletě editoru letáku (sdílená utilita promoSkupiny).
  const [promoFiltr, setPromoFiltr] = useState<PromoFiltr>('vse');
  const [editingId, setEditingId] = useState<string | null>(null); // null = vytváření, jinak úprava existujícího

  const { data: promoImages = [], isLoading } = useQuery({
    queryKey: ['promo-images', user?.role],
    queryFn: () => promoImagesService.getPromoImages(),
  });

  // Admin, předschvalovatel a schvalovatel nejsou vázaní na značky - spravují obsah
  // napříč všemi, takže dostanou celý číselník i volbu „Všechny značky".
  // Dodavatel vidí jen značky, které má přiřazené.
  const bezOmezeniZnacek =
    user?.role === 'admin' || user?.role === 'pre_approver' || user?.role === 'approver';

  // Zpřístupnit promo prodejcům smí stejné role - rozhodnutí 7. 9. 2026.
  // Dodavatel ne; server to kontroluje taky, skryté pole není oprávnění.
  const smiZpristupnitProdejcum = bezOmezeniZnacek;

  // Upravit a smazat i cizí promo: admin, předschvalovatel, schvalovatel.
  // Dodavatel jen své - aby si po sobě mohl uklidit, ale nesahal kolegům.
  const smiSpravovatCizi = bezOmezeniZnacek;

  // Patičky připravují interní role, dodavatel je jen vybírá (4. 11. 2026).
  // Typ zůstane vidět jen u úpravy už existující patičky, aby select nebyl prázdný.
  const smiNahratPaticku = user?.role !== 'supplier';

  const { data: brands = [] } = useQuery({
    queryKey: bezOmezeniZnacek ? ['brands', 'all'] : ['brands', 'my-brands'],
    queryFn: () => (bezOmezeniZnacek ? brandsService.getAllBrands() : brandsService.getMyBrands()),
  });

  // Filtr podle názvu/značky a podle použití (hlavička / promo / patička)
  const filteredPromoImages = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return promoImages.filter(img => {
      const sedneText =
        !query ||
        img.name.toLowerCase().includes(query) ||
        img.brand?.name.toLowerCase().includes(query);
      return sedneText && sedneNaFiltr(img.defaultSize, promoFiltr);
    });
  }, [promoImages, searchQuery, promoFiltr]);

  const uploadMutation = useMutation({
    mutationFn: (data: { name: string; image: File; defaultSize: 'single' | 'horizontal' | 'square' | 'full_page' | 'footer' | 'header_2x1' | 'header_2x2'; brandId: string | null; isForEndUsers?: boolean; fillDate?: boolean }) =>
      promoImagesService.createPromoImage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promo-images', user?.role] });
      setIsUploadModalOpen(false);
      setUploadName('');
      setSelectedFile(null);
      setPreviewUrl(null);
      setSelectedSize('single');
      setSelectedBrandId('');
      setIsForEndUsers(false);
      setFillDate(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => promoImagesService.deletePromoImage(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['promo-images', user?.role] });
      // Když je promo použité v zamčeném letáku, backend ho místo smazání jen skryje.
      if (result?.hidden) {
        alert(result.message || 'Promo bylo skryto z repozitáře (je použité v odeslaném nebo aktivním letáku).');
      }
    },
    onError: (e: any) => {
      alert(e?.response?.data?.message || 'Promo se nepodařilo smazat');
    },
  });

  const resetModal = () => {
    setIsUploadModalOpen(false);
    setEditingId(null);
    setUploadName('');
    setSelectedFile(null);
    setPreviewUrl(null);
    setSelectedSize('single');
    setSelectedBrandId('');
    setIsForEndUsers(false);
    setFillDate(false);
  };

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; image?: File; defaultSize?: 'single' | 'horizontal' | 'square' | 'full_page' | 'footer' | 'header_2x1' | 'header_2x2'; brandId?: string | null; isForEndUsers?: boolean; fillDate?: boolean } }) =>
      promoImagesService.updatePromoImage(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promo-images', user?.role] });
      resetModal();
    },
    onError: (e: any) => {
      alert(e?.response?.data?.message || 'Úpravu se nepodařilo uložit');
    },
  });

  const openCreateModal = () => {
    resetModal();
    // Kdyz je zapnuty filtr na hlavicky nebo paticky, nahrava uzivatel skoro
    // jiste tentyz typ - jen predvyplnime velikost, nabidka zustava uplna.
    if (promoFiltr === 'hlavicka') {
      setSelectedSize('header_2x2');
    } else if (promoFiltr === 'paticka' && smiNahratPaticku) {
      setSelectedSize('footer');
    }
    setIsUploadModalOpen(true);
  };

  const handleEdit = (promo: PromoImage) => {
    setEditingId(promo.id);
    setUploadName(promo.name);
    setSelectedSize(promo.defaultSize as typeof selectedSize);
    setSelectedBrandId(promo.brandId ?? 'all');
    setIsForEndUsers(!!promo.isForEndUsers);
    setFillDate(!!promo.fillDate);
    setSelectedFile(null);
    setPreviewUrl(null);
    setIsUploadModalOpen(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Auto-fill name from filename if empty
      if (!uploadName) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setUploadName(nameWithoutExt);
      }
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!uploadName.trim()) {
      alert('Prosím zadejte název obrázku');
      return;
    }
    // 'all' = "Vsechny znacky" (globalni promo, brandId = null). Jinak musi byt platny UUID znacky.
    const isAllBrands = selectedBrandId === 'all';
    if (!isAllBrands) {
      if (!selectedBrandId || selectedBrandId.trim() === '') {
        alert('Prosím vyberte značku');
        return;
      }

      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(selectedBrandId)) {
        alert('Neplatná značka. Prosím vyberte značku ze seznamu.');
        return;
      }
    }

    // Úprava existujícího promo. Soubor je nepovinný: když se nevybere nový, zůstává
    // stávající obrázek a mění se jen metadata. Poměr stran se kontroluje proti tomu
    // obrázku, který po uložení skutečně zůstane.
    if (editingId) {
      const ratioSource = selectedFile && previewUrl ? previewUrl : getPromoImageUrl(editingId);
      if (!(await confirmRatioOk(ratioSource, selectedSize))) return;
      const replacedId = editingId;
      const replacedFile = selectedFile;
      await updateMutation.mutateAsync({
        id: replacedId,
        data: {
          name: uploadName,
          image: replacedFile ?? undefined,
          defaultSize: selectedSize,
          brandId: isAllBrands ? null : selectedBrandId,
          isForEndUsers,
          fillDate,
        },
      });
      if (replacedFile) {
        setImageStamps((prev) => ({ ...prev, [replacedId]: Date.now() }));
      }
      return;
    }

    if (!selectedFile) {
      alert('Prosím vyberte soubor');
      return;
    }

    // Měkká kontrola poměru stran vůči doporučení pro daný typ (nově nahraný soubor).
    if (previewUrl && !(await confirmRatioOk(previewUrl, selectedSize))) return;

    await uploadMutation.mutateAsync({
      name: uploadName,
      image: selectedFile,
      defaultSize: selectedSize,
      brandId: isAllBrands ? null : selectedBrandId,
      isForEndUsers,
      fillDate,
    });
  };

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Opravdu chcete smazat promo obrázek "${name}"?`)) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const getPromoImageUrl = (id: string) => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
    const base = `${API_URL}/promo-images/${id}/image`;
    const stamp = imageStamps[id];
    return stamp ? `${base}?v=${stamp}` : base;
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  const getSizeLabel = (size: string) => {
    switch (size) {
      case 'single': return '1 slot (1×1)';
      case 'horizontal': return '2 sloty (2×1)';
      case 'square': return '4 sloty (2×2)';
      case 'full_page': return 'Celá stránka';
      case 'footer': return 'Patička';
      case 'header_2x1': return 'Hlavička 2 sloty (2×1)';
      case 'header_2x2': return 'Hlavička 4 sloty (2×2)';
      default: return size;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Promo obrázky</h1>
          <p className="mt-2 text-gray-600">Spravujte propagační obrázky pro letáky</p>
        </div>
        <Button onClick={openCreateModal}>
          <Plus className="w-4 h-4 mr-2" />
          Nahrát obrázek
        </Button>
      </div>

      {/* Search bar */}
      <div className="mb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Hledat podle názvu nebo značky..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        {/* Nízké přepínače vpravo - stejné jako v paletě editoru letáku. */}
        <div className="flex flex-wrap justify-end gap-1 mt-1">
          {PROMO_FILTRY.map(f => {
            const aktivni = promoFiltr === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setPromoFiltr(f.key)}
                aria-pressed={aktivni}
                title={f.key === 'vse' ? 'Bez filtru' : `Zobrazit jen: ${f.label}`}
                className={`px-2 py-0.5 rounded-full border text-xs font-medium transition-colors ${
                  aktivni
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {filteredPromoImages.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-gray-400 mb-4">
            {searchQuery ? (
              <Search className="w-16 h-16 mx-auto" />
            ) : (
              <ImageIcon className="w-16 h-16 mx-auto" />
            )}
          </div>
          {searchQuery ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné výsledky</h3>
              <p className="text-gray-600 mb-6">Zkuste změnit vyhledávací dotaz</p>
              <Button variant="outline" onClick={() => setSearchQuery('')}>
                Vymazat vyhledávání
              </Button>
            </>
          ) : (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné promo obrázky</h3>
              <p className="text-gray-600 mb-6">Začněte nahráním prvního propagačního obrázku</p>
              <Button onClick={openCreateModal}>
                <Plus className="w-4 h-4 mr-2" />
                Nahrát první obrázek
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow divide-y">
          {filteredPromoImages.map((promoImage) => (
            <div key={promoImage.id} className="p-6 hover:bg-gray-50">
              <div className="flex items-start justify-between gap-4">
                {/* min-w-0 musí být na CELÉM řetězci flex položek, jinak se kontejner
                    roztáhne podle obsahu a truncate u názvu se nikdy neuplatní. */}
                <div className="flex space-x-4 flex-1 min-w-0">
                  {/* Thumbnail */}
                  <div className="w-32 h-32 bg-gray-100 rounded-lg flex-shrink-0 overflow-hidden">
                    <img
                      src={getPromoImageUrl(promoImage.id)}
                      alt={promoImage.name}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100" height="100" fill="%23E5E7EB"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%239CA3AF" font-size="12">No Image</text></svg>';
                      }}
                    />
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 truncate" title={promoImage.name}>{promoImage.name}</h3>
                    <div className="text-sm text-gray-600 space-y-1">
                      {promoImage.brand ? (
                        <div className="flex gap-1 min-w-0">
                          <span className="flex-shrink-0">Značka:</span>
                          <span className="text-blue-600 font-medium truncate" title={promoImage.brand.name}>
                            {promoImage.brand.name}
                          </span>
                        </div>
                      ) : (
                        <div>Značka: <span className="text-purple-600 font-medium">Všechny značky</span></div>
                      )}
                      <div>Velikost: {getSizeLabel(promoImage.defaultSize)}</div>
                      {smiZpristupnitProdejcum && (
                        <div>
                          Dostupnost: <span className={promoImage.isForEndUsers ? 'text-green-600' : 'text-gray-600'}>
                            {promoImage.isForEndUsers ? 'Prodejci' : 'Dodavatelé'}
                          </span>
                        </div>
                      )}
                      <div>Vytvořeno: {formatDate(promoImage.createdAt)}</div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-2 flex-shrink-0">
                  {(smiSpravovatCizi || promoImage.supplierId === user?.id) && (
                    <button
                      onClick={() => handleEdit(promoImage)}
                      className="text-blue-600 hover:text-blue-900 p-2 rounded hover:bg-blue-50"
                      title="Upravit promo obrázek"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                  {(smiSpravovatCizi || promoImage.supplierId === user?.id) && (
                    <button
                      onClick={() => handleDelete(promoImage.id, promoImage.name)}
                      className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50"
                      disabled={deleteMutation.isPending}
                      title="Smazat promo obrázek"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      <Modal
        isOpen={isUploadModalOpen}
        onClose={resetModal}
        title={editingId ? 'Upravit promo obrázek' : 'Nahrát promo obrázek'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Název obrázku
            </label>
            <Input
              type="text"
              maxLength={150}
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              placeholder="např. Letní akce, Sleva 50%, ..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Značka *
            </label>
            <select
              value={selectedBrandId}
              onChange={(e) => setSelectedBrandId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Vyberte značku</option>
              {bezOmezeniZnacek && (
                <option value="all">Všechny značky</option>
              )}
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Vyberte značku, ke které promo obrázek patří
              {bezOmezeniZnacek && ' — nebo „Všechny značky“ pro promo nezávislé na značce'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Velikost (kolik slotů zabírá)
            </label>
            <select
              value={selectedSize}
              onChange={(e) => setSelectedSize(e.target.value as 'single' | 'horizontal' | 'square' | 'full_page' | 'footer' | 'header_2x1' | 'header_2x2')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="single">1 slot (1×1)</option>
              <option value="horizontal">2 sloty vodorovně (2×1)</option>
              <option value="square">4 sloty čtverec (2×2)</option>
              <option value="full_page">Celá stránka (8 slotů)</option>
              {(smiNahratPaticku || selectedSize === 'footer') && (
                <option value="footer">Patička (2cm výška, celá šířka)</option>
              )}
              <option value="header_2x1">Hlavička 2 sloty (2×1)</option>
              <option value="header_2x2">Hlavička 4 sloty (2×2)</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Doporučený poměr pro tento typ: <span className="font-medium">{SIZE_SPECS[selectedSize].ratio.toFixed(2)} : 1</span>
              {' '}(např. {SIZE_SPECS[selectedSize].w} × {SIZE_SPECS[selectedSize].h} px). V tomto poměru vyplní slot bez okrajů.
            </p>
          </div>

          {/* Show fillDate checkbox only for footer size */}
          {selectedSize === 'footer' && (
            <div className="flex items-center space-x-2 pt-2">
              <input
                type="checkbox"
                id="fillDate"
                checked={fillDate}
                onChange={(e) => setFillDate(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="fillDate" className="text-sm font-medium text-gray-700">
                Vyplnit datum
              </label>
              <p className="text-xs text-gray-500">
                (Automaticky doplní datum platnosti z letáku bílou barvou vpravo)
              </p>
            </div>
          )}

          {/* Zpřístupnění prodejcům: admin, předschvalovatel a schvalovatel */}
          {smiZpristupnitProdejcum && (
            <div className="flex items-center space-x-2 pt-2">
              <input
                type="checkbox"
                id="isForEndUsers"
                checked={isForEndUsers}
                onChange={(e) => setIsForEndUsers(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="isForEndUsers" className="text-sm font-medium text-gray-700">
                Zobrazit prodejcům
              </label>
              <p className="text-xs text-gray-500">
                (Tento obrázek bude dostupný pro prodejce při vytváření jejich letáků)
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Soubor obrázku
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="w-full"
            >
              <Upload className="w-4 h-4 mr-2" />
              {selectedFile ? selectedFile.name : editingId ? 'Změnit soubor' : 'Vybrat soubor'}
            </Button>
            {editingId && !selectedFile && (
              <p className="mt-1 text-xs text-gray-500">
                Pokud nevyberete nový soubor, zůstane stávající obrázek.
              </p>
            )}
          </div>

          {previewUrl && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Náhled
              </label>
              <div className="border rounded-lg p-2 bg-gray-50">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full h-auto max-h-64 object-contain"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="secondary" onClick={resetModal}>
              Zrušit
            </Button>
            <Button
              onClick={handleUpload}
              isLoading={editingId ? updateMutation.isPending : uploadMutation.isPending}
              disabled={!uploadName.trim() || !selectedBrandId || (!editingId && !selectedFile)}
            >
              {editingId ? 'Uložit změny' : 'Nahrát'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
